// Motion primitives shared by the depth/* components and marketing pages.
// Kept in a non-component module so files importing them stay Fast-Refresh-clean
// (a .tsx file that exports both components and constants breaks HMR).
import { useScroll, useTransform, type MotionValue, type Variants } from 'framer-motion';
import type { RefObject } from 'react';

/* ──────────────────────────────────────────────────────────────────────────
 * Reveal helpers — opinionated whileInView variants with stagger.
 * ────────────────────────────────────────────────────────────────────────── */
export const containerStagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};
export const fadeUpItem: Variants = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
export const fadeRightItem: Variants = {
  hidden: { opacity: 0, x: -28 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
export const fadeLeftItem: Variants = {
  hidden: { opacity: 0, x: 28 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};
export const scaleItem: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

/* ──────────────────────────────────────────────────────────────────────────
 * useParallaxY — hook for elements that should drift on scroll.
 * Returns a MotionValue<number> bound to the section ref.
 * ────────────────────────────────────────────────────────────────────────── */
export function useParallaxY(
  ref: RefObject<HTMLElement | null>,
  range: [number, number] = [-60, 60],
): MotionValue<number> {
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  return useTransform(scrollYProgress, [0, 1], range);
}
